import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, Play, Pause } from 'lucide-react';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react/dist/iconify.js';
import CustomSlider from '../child/CustomSlider';
import InfoTooltip from '../child/InfoToolkitTip';
import { VOICE_PROVIDERS } from '../../utils/assistants/voice_config';
import { BACKGROUND_SOUNDS_OPTIONS } from '../../utils/assistants/base';
import { toast } from 'sonner';
import { useSelector, useDispatch } from 'react-redux';
import { appendToAssistantDetail } from '../../store/app/assistantSlice';

function VoiceComponent() {
    const activeDetails = useSelector((state) => state.assistants.activeDetail);
    const dispatch = useDispatch();

    const [provider, setProvider] = useState('');
    const [voice, setVoice] = useState('');
    const [backgroundSound, setBackgroundSound] = useState('');
    const [customSoundUrl, setCustomSoundUrl] = useState('');
    const [isPlaying, setIsPlaying] = useState(false);

    const audioRef = useRef(null);

    const isFormValid = provider && voice;
    const providerVoices = provider ? VOICE_PROVIDERS[provider] || [] : [];

    const formatVoice = (voiceId) => {
        if (!voiceId) return '';
        return voiceId.split(' ')[0].charAt(0).toUpperCase() + voiceId.split(' ')[0].slice(1);
    };

    useEffect(() => {
        const v = activeDetails?.details?.voice;
        const bg = activeDetails?.details?.backgroundSound;
        const custom = activeDetails?.details?.customSoundUrl;

        if (v?.provider) setProvider(v.provider);
        if (v?.voiceId) setVoice(formatVoice(v.voiceId));
        if (bg) setBackgroundSound(bg);
        if (custom) setCustomSoundUrl(custom);
    }, [activeDetails]);

    useEffect(() => {
        if (!provider || !voice) return;

        let payload = {
            voice: {
                voiceId: voice.toLowerCase(),
                provider
            }
        };

        if (backgroundSound !== '') {
            payload.backgroundSound = backgroundSound;
        }

        if (backgroundSound === 'custom' && customSoundUrl) {
            payload.customSoundUrl = customSoundUrl;
        }

        dispatch(appendToAssistantDetail(payload));
    }, [provider, voice, backgroundSound, customSoundUrl]);

    const getSelectedVoiceObject = () => {
        return providerVoices.find(v => v.label.split(" ")[0] === voice);
    };

    const handlePlayPause = () => {
        const voiceObj = getSelectedVoiceObject();

        if (!voiceObj?.demoUrl) {
            toast.error("No demo audio available for selected voice.");
            return;
        }

        // If already playing, pause it
        if (isPlaying) {
            if (audioRef.current) {
                audioRef.current.pause();
            }
            setIsPlaying(false);
        } else {
            // If not playing, play it
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            const audio = new Audio(voiceObj.demoUrl);
            audioRef.current = audio;

            audio.play().then(() => {
                setIsPlaying(true);
            }).catch(err => {
                console.error("Playback failed", err);
                toast.error("Audio playback failed.");
            });

            audio.onended = () => {
                setIsPlaying(false);
            };
        }
    };

    return (
        <div className="card p-20 m-10">
            <div className="text-lg font-semibold mb-2">Voice Configuration</div>
            <div className='text-xs mb-10'>Select a voice or sync your library.</div>

            <div className='flex gap-2'>
                {/* Provider Select */}
                <div style={{ position: 'relative', flex: 1 }}>
                    <select
                        id='llm-provider'
                        className='agent-form-input flex-1'
                        style={{ paddingRight: '2.5rem', appearance: 'none' }}
                        value={provider}
                        onChange={(e) => {
                            setProvider(e.target.value);
                            setVoice('');
                            setIsPlaying(false);
                            if (audioRef.current) audioRef.current.pause();
                        }}
                    >
                        <option disabled value="">Select Provider</option>
                        {Object.keys(VOICE_PROVIDERS).map((prov, i) => (
                            <option key={i} value={prov}>{prov}</option>
                        ))}
                    </select>
                    <ChevronDown
                        size={20}
                        style={{
                            position: 'absolute',
                            right: '12px',
                            top: '40%',
                            transform: 'translateY(-50%)',
                            color: '#9CA3AF',
                            pointerEvents: 'none'
                        }}
                    />
                </div>

                {/* Voice Select */}
                <div style={{ position: 'relative', flex: 1 }}>
                    <select
                        id='voice-selector'
                        className='agent-form-input flex-1'
                        style={{ paddingRight: '2.5rem', appearance: 'none' }}
                        value={voice}
                        onChange={(e) => {
                            setVoice(e.target.value);
                            setIsPlaying(false);
                            if (audioRef.current) audioRef.current.pause();
                        }}
                        disabled={!provider}
                    >
                        <option disabled value="">Voices</option>
                        {providerVoices.map((v, i) => (
                            <option key={i} value={v.label.split(" ")[0]}>
                                {v.label.split(" ")[0]}
                            </option>
                        ))}
                    </select>
                    <ChevronDown
                        size={20}
                        style={{
                            position: 'absolute',
                            right: '12px',
                            top: '40%',
                            transform: 'translateY(-50%)',
                            color: '#9CA3AF',
                            pointerEvents: 'none'
                        }}
                    />
                </div>

                {/* Play/Pause Button */}
                <motion.button
                    whileHover={isFormValid ? { scale: 1.05 } : {}}
                    whileTap={isFormValid ? { scale: 0.95 } : {}}
                    disabled={!isFormValid}
                    className="submit-btn-style"
                    style={{
                        height: '43px',
                        opacity: isFormValid ? 1 : 0.5,
                        cursor: isFormValid ? 'pointer' : 'not-allowed'
                    }}
                    onClick={handlePlayPause}
                >
                    {isPlaying ? <Pause size={20} color="white" /> : <Play size={20} color="white" />}
                </motion.button>
            </div>

            {/* Additional Config */}
            <div className='mt-10 font-medium'>Additional Configuration</div>
            <div className='text-xs mb-10'>Configure additional settings for the voice of your assistant</div>

            {/* Background Sound */}
            <div className='text-sm mb-2'>Background Sound</div>
            <div style={{ position: 'relative', flex: 1 }}>
                <select
                    id='background-sound-selector'
                    className='agent-form-input flex-1'
                    style={{ paddingRight: '2.5rem', appearance: 'none' }}
                    value={backgroundSound}
                    onChange={(e) => setBackgroundSound(e.target.value)}
                >
                    <option disabled value="">Select background sound</option>
                    {BACKGROUND_SOUNDS_OPTIONS.map((opt, i) => (
                        <option key={i} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <ChevronDown
                    size={20}
                    style={{
                        position: 'absolute',
                        right: '12px',
                        top: '40%',
                        transform: 'translateY(-50%)',
                        color: '#9CA3AF',
                        pointerEvents: 'none'
                    }}
                />
            </div>

            {/* Background Sound URL Info */}
            <div className='text-sm mb-2 flex gap-20'>
                <div>Background Sound URL</div>
                <InfoTooltip message='You can use a public URL of an audio file. You must select the custom option in the background sound dropdown to use this feature.' />
            </div>
            <div className="transcriber-input">
                <input
                    id="chip-input"
                    type="text"
                    className="transcriber-input-field"
                    placeholder="http://www.example.com/sound.mp3"
                />
            </div>

            {/* Custom Sound URL */}
            {backgroundSound === 'custom' && (
                <div className='mt-4'>
                    <div className='flex gap-2 mb-2 text-sm'>
                        <div>Background Sound URL</div>
                        <InfoTooltip message='You can use a public URL of an audio file. You must select the custom option in the background sound dropdown to use this feature.' />
                    </div>
                    <div className='transcriber-input'>
                        <input
                            id="custom-sound-url"
                            type="text"
                            className="transcriber-input-field"
                            placeholder="Custom Sound URL"
                            value={customSoundUrl}
                            onChange={(e) => setCustomSoundUrl(e.target.value)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default VoiceComponent;
